<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useLoadRoleObjectTypes } from '@/features/Role/RoleEdit'
import type { TypePermission } from '@/entities/Role'

const props = defineProps<{
  modelValue: TypePermission[]
  groupId: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: TypePermission[]): void
}>()

const show = defineModel<boolean>('show', { default: false })

const objectTypesQuery = useLoadRoleObjectTypes(computed(() => props.groupId))
const objectTypes = computed(() => {
  const payload = objectTypesQuery.data.value?.payload
  const raw = Array.isArray(payload?.object_types) ? payload.object_types : []
  return raw
    .map((item: any) => ({
      id: Number(item.id),
      name: String(item.name || item.object_name || `#${item.id}`),
      is_system: Boolean(item.is_system),
    }))
    .filter((item: any) => Number.isFinite(item.id))
    .sort((a: any, b: any) => a.name.localeCompare(b.name))
})

const manualsTypes = computed(() => objectTypes.value.filter((t: any) => t.is_system))
const elementsTypes = computed(() => objectTypes.value.filter((t: any) => !t.is_system))

// localPerms only holds types that are explicitly in custom (restricted) mode.
// Types NOT in localPerms are in default (unrestricted) mode.
const localPerms = ref<Record<number, TypePermission>>({})

const isCustom = (typeId: number): boolean => !!localPerms.value[typeId]

const ensurePerm = (objectTypeId: number): TypePermission => {
  if (!localPerms.value[objectTypeId]) {
    localPerms.value[objectTypeId] = {
      object_type_id: objectTypeId,
      read: true,
      create: true,
      update: true,
      delete: true,
    }
  }
  return localPerms.value[objectTypeId]
}

const restrictType = (typeId: number) => {
  ensurePerm(typeId)
}

const resetType = (typeId: number) => {
  const copy = { ...localPerms.value }
  delete copy[typeId]
  localPerms.value = copy
}

const initLocalPerms = () => {
  const map: Record<number, TypePermission> = {}
  for (const p of props.modelValue ?? []) {
    map[p.object_type_id] = { ...p }
  }
  localPerms.value = map
}

watch(show, (visible) => {
  if (visible) initLocalPerms()
})

watch(() => props.modelValue, () => {
  if (show.value) initLocalPerms()
})

watch(() => objectTypes.value, () => {
  if (show.value) initLocalPerms()
})

const onReadChange = (typeId: number, value: boolean) => {
  const p = ensurePerm(typeId)
  p.read = value
  if (!value) {
    p.create = false
    p.update = false
    p.delete = false
  }
}

const save = () => {
  // Emit ALL types that are in custom mode, even if all flags are false
  // (explicit deny-all is a valid state). Types not in localPerms use
  // default unrestricted behaviour and are not stored.
  const result = Object.values(localPerms.value)
  emit('update:modelValue', result)
  show.value = false
}
</script>

<template>
  <VDialog v-model="show" max-width="720">
    <VCard>
      <VCardTitle class="text-h6">Права доступа к справочникам</VCardTitle>

      <VCardText class="pa-4" style="max-height: 70vh; overflow-y: auto;">
        <VExpansionPanels multiple>
          <VExpansionPanel>
            <VExpansionPanelTitle>Справочники</VExpansionPanelTitle>
            <VExpansionPanelText>
              <div
                v-for="t in manualsTypes"
                :key="t.id"
                class="d-flex align-center ga-4 py-2"
              >
                <span class="flex-grow-1" style="min-width: 180px">{{ t.name }}</span>

                <template v-if="!isCustom(t.id)">
                  <span class="text-caption text-success">Полный доступ</span>
                  <VBtn
                    size="small"
                    variant="tonal"
                    color="primary"
                    @click="restrictType(t.id)"
                  >
                    Ограничить
                  </VBtn>
                </template>

                <template v-else>
                  <VCheckbox
                    :model-value="localPerms[t.id]?.read ?? false"
                    @update:model-value="(v) => onReadChange(t.id, Boolean(v))"
                    label="Чтение"
                    density="compact"
                    hide-details
                  />
                  <VCheckbox
                    :model-value="localPerms[t.id]?.create ?? false"
                    @update:model-value="(v) => { ensurePerm(t.id).create = Boolean(v) }"
                    label="Создание"
                    density="compact"
                    hide-details
                    :disabled="!localPerms[t.id]?.read"
                  />
                  <VCheckbox
                    :model-value="localPerms[t.id]?.update ?? false"
                    @update:model-value="(v) => { ensurePerm(t.id).update = Boolean(v) }"
                    label="Изменение"
                    density="compact"
                    hide-details
                    :disabled="!localPerms[t.id]?.read"
                  />
                  <VCheckbox
                    :model-value="localPerms[t.id]?.delete ?? false"
                    @update:model-value="(v) => { ensurePerm(t.id).delete = Boolean(v) }"
                    label="Удаление"
                    density="compact"
                    hide-details
                    :disabled="!localPerms[t.id]?.read"
                  />
                  <VBtn
                    size="small"
                    variant="text"
                    color="error"
                    @click="resetType(t.id)"
                  >
                    Сбросить
                  </VBtn>
                </template>
              </div>
              <div v-if="!manualsTypes.length" class="text-caption text-grey">
                Нет доступных справочников
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>

          <VExpansionPanel>
            <VExpansionPanelTitle>Редактор форм</VExpansionPanelTitle>
            <VExpansionPanelText>
              <div
                v-for="t in elementsTypes"
                :key="t.id"
                class="d-flex align-center ga-4 py-2"
              >
                <span class="flex-grow-1" style="min-width: 180px">{{ t.name }}</span>

                <template v-if="!isCustom(t.id)">
                  <span class="text-caption text-success">Полный доступ</span>
                  <VBtn
                    size="small"
                    variant="tonal"
                    color="primary"
                    @click="restrictType(t.id)"
                  >
                    Ограничить
                  </VBtn>
                </template>

                <template v-else>
                  <VCheckbox
                    :model-value="localPerms[t.id]?.read ?? false"
                    @update:model-value="(v) => onReadChange(t.id, Boolean(v))"
                    label="Чтение"
                    density="compact"
                    hide-details
                  />
                  <VCheckbox
                    :model-value="localPerms[t.id]?.create ?? false"
                    @update:model-value="(v) => { ensurePerm(t.id).create = Boolean(v) }"
                    label="Создание"
                    density="compact"
                    hide-details
                    :disabled="!localPerms[t.id]?.read"
                  />
                  <VCheckbox
                    :model-value="localPerms[t.id]?.update ?? false"
                    @update:model-value="(v) => { ensurePerm(t.id).update = Boolean(v) }"
                    label="Изменение"
                    density="compact"
                    hide-details
                    :disabled="!localPerms[t.id]?.read"
                  />
                  <VCheckbox
                    :model-value="localPerms[t.id]?.delete ?? false"
                    @update:model-value="(v) => { ensurePerm(t.id).delete = Boolean(v) }"
                    label="Удаление"
                    density="compact"
                    hide-details
                    :disabled="!localPerms[t.id]?.read"
                  />
                  <VBtn
                    size="small"
                    variant="text"
                    color="error"
                    @click="resetType(t.id)"
                  >
                    Сбросить
                  </VBtn>
                </template>
              </div>
              <div v-if="!elementsTypes.length" class="text-caption text-grey">
                Нет доступных элементов
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn color="default" variant="tonal" @click="show = false">Отмена</VBtn>
        <VBtn color="primary" @click="save">Сохранить</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
